import { Grid } from '@focaldata/cin-ui-components'
import React from 'react'
import {
  ForkFilterOption,
  NewFilterOption,
  SelectedFilter
} from '../../data/model/results'
import useStyles from '../Filters/Filters.styles' // Reuse existing styles
import AnalyticsFilterBlock, {
  AnalyticsResponseOptionSelection
} from './AnalyticsFilterBlock'

interface Props {
  filters: NewFilterOption[]
  forkFilters: ForkFilterOption[]
  selectedFilters: SelectedFilter[]
  onDeleteFilter: (
    filterId: string,
    isFork: boolean,
    marketLabel?: string | null
  ) => void
  onToggleResponseOption: (selection: AnalyticsResponseOptionSelection) => void
  isLoading: boolean
}

const AnalyticsSelectedFilters: React.FC<Props> = (props: Props) => {
  const {
    filters,
    forkFilters,
    selectedFilters,
    onDeleteFilter,
    onToggleResponseOption,
    isLoading
  } = props

  const { classes } = useStyles()

  // Show loading only on initial load when we have no data yet
  if (
    isLoading &&
    filters.length === 0 &&
    forkFilters.length === 0 &&
    selectedFilters.length === 0
  ) {
    return (
      <div className={classes.selectedEntries}>Loading selected filters...</div>
    )
  }

  // Get all filters (questions + forks) that are currently selected
  // Forks are always displayed at the top
  const getSelectedFilterBlocks = (): JSX.Element[] => {
    const blocks: JSX.Element[] = []

    // Add selected fork filters first (always at the top)
    selectedFilters.forEach((selectedFilter) => {
      if (selectedFilter.forkId) {
        const filter = forkFilters.find((f) => f.id === selectedFilter.forkId)
        if (filter) {
          blocks.push(
            <Grid
              item
              xs={12}
              key={`fork-${selectedFilter.forkId}`}
              className={classes.filterBlockContainer}
            >
              <AnalyticsFilterBlock
                filter={filter}
                selectedFilter={selectedFilter}
                onDelete={onDeleteFilter}
                onToggleResponseOption={onToggleResponseOption}
              />
            </Grid>
          )
        }
      }
    })

    // Add selected question filters after forks
    selectedFilters.forEach((selectedFilter) => {
      if (selectedFilter.questionId) {
        // A questionId alone can match two combined-view entries split across
        // market clusters (disjoint option sets) — marketLabel disambiguates.
        const filter = filters.find(
          (f) =>
            f.questionId === selectedFilter.questionId &&
            (f.marketLabel ?? null) === (selectedFilter.marketLabel ?? null)
        )
        if (filter) {
          blocks.push(
            <Grid
              item
              xs={12}
              key={`question-${selectedFilter.questionId}-${
                selectedFilter.matrixTitleId || ''
              }-${selectedFilter.marketLabel || ''}`}
              className={classes.filterBlockContainer}
            >
              <AnalyticsFilterBlock
                filter={filter}
                selectedFilter={selectedFilter}
                onDelete={onDeleteFilter}
                onToggleResponseOption={onToggleResponseOption}
              />
            </Grid>
          )
        }
      }
    })

    return blocks
  }

  const selectedFilterBlocks = getSelectedFilterBlocks()

  return (
    <div className={classes.selectedEntries}>
      {selectedFilterBlocks.length > 0 ? (
        <Grid container spacing={1}>
          {selectedFilterBlocks}
        </Grid>
      ) : null}
    </div>
  )
}

export default AnalyticsSelectedFilters
